/**
 * SaveAsDefaultWatchlistModal — confirm setting the currently-selected
 * watchlist as the user's default (loaded on next login).
 */
import Modal, { Styles } from "react-modal";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { HiOutlineBookmark } from "react-icons/hi";
import { saveAsUserDefaultWatchlist } from "@service/watchlist-handler";
import { getColorTheme } from "@service/local-storage-handler";
import "../watchlist-modal-styles.css";

Modal.setAppElement("#marketview");

const SaveAsDefaultWatchlistModal = ({
  saveAsDefaultWatchlistModalIsOpen,
  setSaveAsDefaultWatchlistModalIsOpen,
}) => {
  const { selectedWatchlist } = useSelector(({ watchlist }) => watchlist);

  const themeFromStore = useSelector(({ theme }) => theme.theme);
  // Default to dark if theme is not set in Redux (fallback to localStorage)
  const theme = themeFromStore || getColorTheme() || "dark";

  const closeModal = () => {
    setSaveAsDefaultWatchlistModalIsOpen(false);
  };

  const handleNoButton = () => {
    closeModal();
  };

  const handleYesButton = (event) => {
    event.preventDefault();
    saveAsUserDefaultWatchlist(selectedWatchlist.value)
      .then(() => {
        toast.success(
          (t) => (
            <div className={`toast-msgs-container ${theme}`}>
              <div>
                <label
                  htmlFor="toast-msg"
                  style={{ color: theme === "dark" ? "#f7fafc" : "#1a202c" }}
                >
                  Default watchlist saved successfully
                </label>
              </div>
              <div>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  className="dismiss-btn"
                >
                  Dismiss
                </button>
              </div>
            </div>
          ),
          {
            duration: 6000,
            style: {
              fontSize: "0.8rem",
              borderRadius: 0,
              textAlign: "left",
              paddingRight: 0,
            },
          }
        );
      })
      .catch((error) => {
        toast.error(
          (t) => (
            <div className={`toast-msgs-container ${theme}`}>
              <div>
                <label
                  htmlFor="toast-msg"
                  style={{ color: theme === "dark" ? "#f7fafc" : "#1a202c" }}
                >
                  {`Error while saving default watchlist. ${error}`}
                </label>
              </div>
              <div>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  className="dismiss-btn"
                >
                  Dismiss
                </button>
              </div>
            </div>
          ),
          {
            duration: 6000,
            style: {
              fontSize: "0.8rem",
              borderRadius: 0,
              textAlign: "left",
              paddingRight: 0,
            },
          }
        );
      })
      .finally(() => {
        closeModal();
      });
  };

  return (
    <>
      <Modal
        isOpen={saveAsDefaultWatchlistModalIsOpen}
        onRequestClose={() => setSaveAsDefaultWatchlistModalIsOpen(false)}
        style={getModalStyles(theme)}
      >
        <div className={`wl-modal ${theme}`}>
          {/* Coloured header banner */}
          <div className="wl-modal__header wl-modal__header--default">
            <div className="wl-modal__header-bg wl-modal__header-bg--default" />
            <div className="wl-modal__icon-wrap">
              <div className="wl-modal__icon">
                <HiOutlineBookmark />
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="wl-modal__body">
            <h3 className="wl-modal__title">Set Default Watchlist</h3>
            <div className="wl-modal__divider" />
            <p className="wl-modal__desc">
              Do you want to set{" "}
              <span className="wl-modal__highlight">{selectedWatchlist?.label}</span>{" "}
              as your default watchlist?
            </p>
          </div>

          {/* Footer */}
          <div className="wl-modal__footer">
            <button type="button" className="wl-modal__btn wl-modal__btn--cancel" onClick={handleNoButton}>
              Cancel
            </button>
            <button type="button" className="wl-modal__btn wl-modal__btn--proceed" onClick={handleYesButton}>
              Yes, Proceed
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SaveAsDefaultWatchlistModal;

const getModalStyles = (theme: string): Styles => ({
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme === "dark" ? "rgba(0,0,0,0.75)" : "rgba(15,23,42,0.55)",
    zIndex: 10001,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
  },
  content: {
    position: "relative",
    top: "auto",
    left: "auto",
    right: "auto",
    bottom: "auto",
    border: "none",
    background: "transparent",
    overflow: "visible",
    WebkitOverflowScrolling: "touch",
    borderRadius: "0",
    outline: "none",
    padding: "0",
    maxWidth: "400px",
    width: "90%",
  },
});
